import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Colors } from "../../../Colors";
import { StyledTextField } from "../../../components/text-fields/StyledTextField";
import { NumberInput } from "../../../subcomponents/NumberInput";
import { AutoComplete, AutoCompleteCheckbox } from "../../../subcomponents/AutoComplete";
import { RootState, useAppDispatch } from "../../../store";
import {
  addProvider,
  fetchInsurances,
  updateProvider,
} from "../../../slices/ProviderSlice";
import {
  AppointmentType,
  BookableTimeRanges,
  Insurance,
  Provider,
  RawWebptAvailableCalendar,
} from "../../../types";
import { filterAppointmentTypesForPicker } from "../../../utils/appointmentTypes";
import BookableTimeRangesSection from "./BookableTimeRanges";
import InsuranceAcceptedSelect from "./InsuranceAcceptedSelect";

const sectionLabelSx = {
  color: Colors.coolWhite,
  fontSize: "0.813rem",
  fontWeight: 600,
  letterSpacing: "0.2px",
  mb: 1,
};

const containedButtonSx = {
  textTransform: "none" as const,
  fontSize: "0.875rem",
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

type CalendarOption = { label: string; value: string };

type Props = {
  open: boolean;
  mode: "add" | "edit";
  provider?: Provider;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

const emptyTimeRanges: BookableTimeRanges = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

export default function ProviderFormModal({
  open,
  mode,
  provider,
  onClose,
  onSuccess,
}: Props) {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const { providers, insuranceOptions, loading } = useSelector(
    (state: RootState) => state.providers
  );

  const [providerName, setProviderName] = useState("");
  const [selectedCalendar, setSelectedCalendar] = useState<CalendarOption | null>(
    null
  );
  const [webptCalendars, setWebptCalendars] = useState<RawWebptAvailableCalendar[]>(
    []
  );
  const [loadingCalendars, setLoadingCalendars] = useState(false);

  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [selectedInsuranceIds, setSelectedInsuranceIds] = useState<string[]>([]);
  const [selectedAppointmentTypeIds, setSelectedAppointmentTypeIds] = useState<
    string[]
  >([]);
  const [isSupervisingProvider, setIsSupervisingProvider] = useState(false);
  const [maxSimultaneousPatients, setMaxSimultaneousPatients] = useState(1);
  const [bookableTimeRanges, setBookableTimeRanges] =
    useState<BookableTimeRanges>(emptyTimeRanges);

  const [formError, setFormError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch supporting data each time the modal opens.
  useEffect(() => {
    if (!open || !user?.token) return;
    dispatch(fetchInsurances(user.token));
  }, [open, user?.token, dispatch]);

  useEffect(() => {
    if (!open || !user?.token || !organization?.organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`,
          {
            headers: { Authorization: `Bearer ${user.token}` },
            params: { organizationId: organization.organizationId },
          }
        );
        if (!cancelled) setAppointmentTypes(response.data);
      } catch (err) {
        console.error("Error fetching appointment types:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user?.token, organization?.organizationId]);

  // Fetch webpt calendars whenever the modal opens — needed in add mode for the
  // selector and in edit mode to display the calendar's true WebPT title.
  useEffect(() => {
    if (!open || !user?.token) return;
    let cancelled = false;
    (async () => {
      setLoadingCalendars(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/providers/webpt`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        if (!cancelled) setWebptCalendars(response.data || []);
      } catch (err) {
        console.error("Error fetching WebPT calendars:", err);
        if (!cancelled && mode === "add")
          setFormError("Failed to load WebPT calendars.");
      } finally {
        if (!cancelled) setLoadingCalendars(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, user?.token]);

  // Reset / hydrate form whenever the modal opens, mode changes, or provider changes.
  // All form state — including the insurance and appointment-type ID arrays —
  // is set here in a single pass keyed only on `[open, mode, provider]`. We
  // deliberately do NOT depend on `insuranceOptions` or `appointmentTypes`,
  // which refetch on every modal open: re-running this effect on those refs
  // would silently overwrite the user's in-flight edits.
  useEffect(() => {
    if (!open) return;
    setFormError("");

    if (mode === "edit" && provider) {
      setProviderName(provider.providerName ?? "");
      setSelectedCalendar({
        label: provider.providerName ?? "",
        value: provider.ehrProviderId ?? "",
      });
      setIsSupervisingProvider(Boolean(provider.isSupervisingProvider));
      setMaxSimultaneousPatients(
        typeof provider.maxSimultaneousPatients === "number" &&
          !Number.isNaN(provider.maxSimultaneousPatients)
          ? provider.maxSimultaneousPatients
          : 1
      );
      setBookableTimeRanges({
        ...emptyTimeRanges,
        ...(provider.bookableTimeRanges || {}),
      });
      setSelectedInsuranceIds(provider.insurancesAccepted || []);
      setSelectedAppointmentTypeIds(provider.appointmentTypesAccepted || []);
    } else {
      setProviderName("");
      setSelectedCalendar(null);
      setSelectedInsuranceIds([]);
      setSelectedAppointmentTypeIds([]);
      setIsSupervisingProvider(false);
      setMaxSimultaneousPatients(1);
      setBookableTimeRanges(emptyTimeRanges);
    }
  }, [open, mode, provider]);

  const appointmentTypeOptions: CalendarOption[] = useMemo(
    () =>
      filterAppointmentTypesForPicker(
        appointmentTypes,
        selectedAppointmentTypeIds,
        organization?.organizationId
      ).map((t) => ({
        label: t.name,
        value: t.appointmentTypeId.toString(),
      })),
    [appointmentTypes, selectedAppointmentTypeIds]
  );

  const calendarOptions: CalendarOption[] = useMemo(() => {
    const usedCalendarIds = new Set(providers.map((p) => p.ehrProviderId));
    return webptCalendars
      .filter((cal) => !usedCalendarIds.has(cal.Cal_CalendarID.toString()))
      .map((cal) => ({
        label: cal.Cal_Title,
        value: cal.Cal_CalendarID.toString(),
      }));
  }, [providers, webptCalendars]);

  // Render-time projections of the ID source-of-truth onto the UI shapes.
  // Items whose option hasn't loaded yet (or no longer exists) are dropped
  // from the visible list, but their IDs stay in state and submit unchanged.
  const selectedInsurances: Insurance[] = useMemo(
    () =>
      selectedInsuranceIds
        .map((id) => insuranceOptions.find((ins) => ins.insuranceId === id))
        .filter((ins): ins is Insurance => Boolean(ins)),
    [selectedInsuranceIds, insuranceOptions]
  );

  const selectedAppointmentTypes: CalendarOption[] = useMemo(
    () =>
      selectedAppointmentTypeIds
        .map((id) => appointmentTypeOptions.find((opt) => opt.value === id))
        .filter((opt): opt is CalendarOption => Boolean(opt)),
    [selectedAppointmentTypeIds, appointmentTypeOptions]
  );

  const webptCalendarMap = useMemo(() => {
    const map = new Map<string, string>();
    webptCalendars.forEach((cal) =>
      map.set(cal.Cal_CalendarID.toString(), cal.Cal_Title)
    );
    return map;
  }, [webptCalendars]);

  const editModeWebptTitle =
    mode === "edit" && provider
      ? webptCalendarMap.get(provider.ehrProviderId)
      : undefined;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    if (!user?.token) {
      setFormError("You must be logged in.");
      return;
    }

    if (mode === "add" && !selectedCalendar?.value) {
      setFormError("Please select a WebPT calendar.");
      return;
    }

    const finalName =
      providerName.trim() || selectedCalendar?.label.trim() || "";
    if (!finalName) {
      setFormError("Provider name is required.");
      return;
    }

    if (!Number.isFinite(maxSimultaneousPatients) || maxSimultaneousPatients < 1) {
      setFormError("Max simultaneous patients must be at least 1.");
      return;
    }

    for (const [day, ranges] of Object.entries(bookableTimeRanges)) {
      for (const range of ranges || []) {
        if (!range.startTime || !range.endTime) {
          setFormError(`Set both start and end time for all ranges in ${day}.`);
          return;
        }
        if (range.startTime >= range.endTime) {
          setFormError(`Start time must be before end time on ${day}.`);
          return;
        }
      }
    }

    const payload: Partial<Provider> = {
      ehrProviderId:
        mode === "edit"
          ? provider!.ehrProviderId
          : selectedCalendar!.value.toString(),
      providerName: finalName,
      insurancesAccepted: selectedInsuranceIds,
      appointmentTypesAccepted: selectedAppointmentTypeIds,
      isSupervisingProvider,
      maxSimultaneousPatients,
      minGapBetweenVisits: 0,
      bookableTimeRanges,
    };

    setSubmitting(true);
    try {
      if (mode === "add") {
        await dispatch(
          addProvider({ provider: payload, token: user.token })
        ).unwrap();
        onSuccess("Provider added successfully");
      } else {
        await dispatch(
          updateProvider({
            providerId: provider!.providerId,
            provider: { ...payload, providerId: provider!.providerId },
            token: user.token,
          })
        ).unwrap();
        onSuccess("Provider updated successfully");
      }
      onClose();
    } catch (err) {
      setFormError(
        mode === "add"
          ? "Failed to add provider. Please try again."
          : "Failed to update provider. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  const isBusy = submitting || loading;

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: Colors.bg2,
          color: "#FFF",
          borderRadius: "12px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          pb: 1,
        }}
      >
        {mode === "add" ? "Add Provider" : "Edit Provider"}
        <IconButton
          onClick={onClose}
          disabled={isBusy}
          sx={{ color: Colors.info }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <form onSubmit={handleSubmit} id="provider-form">
          {formError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
              {formError}
            </Alert>
          )}

          {/* WebPT calendar */}
          <Box display="flex" flexDirection="column" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={sectionLabelSx}>
              WebPT Calendar
            </Typography>
            {mode === "add" ? (
              <AutoComplete
                label="Calendars"
                placeholder={
                  loadingCalendars
                    ? "Loading available calendars..."
                    : "Select a calendar"
                }
                options={calendarOptions}
                value={selectedCalendar}
                setValue={(value) => {
                  const next = value as CalendarOption | null;
                  setSelectedCalendar(next);
                  // Pre-fill the editable Penciled label with the WebPT title
                  // when the user hasn't customized it yet.
                  const previousLabel = selectedCalendar?.label.trim() ?? "";
                  if (!providerName.trim() || providerName.trim() === previousLabel) {
                    setProviderName(next?.label ?? "");
                  }
                }}
              />
            ) : (
              <Box
                sx={{
                  py: 1.25,
                  px: 1.75,
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  backgroundColor: Colors.textfield,
                  fontSize: "0.875rem",
                  color: editModeWebptTitle
                    ? Colors.coolWhite
                    : Colors.info,
                  fontStyle:
                    !editModeWebptTitle && !loadingCalendars
                      ? "italic"
                      : "normal",
                }}
              >
                {loadingCalendars
                  ? "Loading..."
                  : editModeWebptTitle || "Unknown calendar"}{" "}
                <Typography
                  component="span"
                  sx={{ color: Colors.info, fontSize: "0.75rem", ml: 1 }}
                >
                  Calendar ID: {provider?.ehrProviderId || "—"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Provider name (Penciled label, editable in both modes) */}
          <Box display="flex" flexDirection="column" sx={{ mb: 3, maxWidth: "320px" }}>
            <Typography variant="body2" sx={sectionLabelSx}>
              Provider Name
            </Typography>
            <StyledTextField
              value={providerName}
              onChange={(value) => setProviderName(value)}
            />
          </Box>

          {/* Insurance */}
          <InsuranceAcceptedSelect
            value={selectedInsurances}
            onChange={(next) =>
              setSelectedInsuranceIds(next.map((i) => i.insuranceId))
            }
            options={insuranceOptions || []}
          />

          {/* Appointment types */}
          <Box display="flex" flexDirection="column" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={sectionLabelSx}>
              Appointment Types Accepted
            </Typography>
            <AutoCompleteCheckbox
              label=""
              placeholder="Select appointment types"
              options={appointmentTypeOptions}
              value={selectedAppointmentTypes}
              setValue={(next) =>
                setSelectedAppointmentTypeIds(next.map((t) => t.value))
              }
            />
          </Box>

          {/* Supervising */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mb: 3,
              py: 1.5,
              px: 2.5,
              borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              maxWidth: "520px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: Colors.coolWhite, fontWeight: 600, fontSize: "0.875rem" }}
            >
              Supervising PT
            </Typography>
            <Switch
              checked={isSupervisingProvider}
              onChange={(event) => setIsSupervisingProvider(event.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#FFFFFF",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#FFFFFF",
                  "& + .MuiSwitch-track": {
                    backgroundColor: Colors.success,
                  },
                },
              }}
            />
          </Box>

          {/* Max patients */}
          <NumberInput
            label="Max number of simultaneous patients"
            disabled={isBusy}
            value={maxSimultaneousPatients}
            setter={(value) => {
              if (Number.isFinite(value)) setMaxSimultaneousPatients(value);
            }}
          />

          {/* Bookable time ranges */}
          <BookableTimeRangesSection
            value={bookableTimeRanges}
            onChange={setBookableTimeRanges}
          />
        </form>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          p: 2.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isBusy}
          sx={{ textTransform: "none", color: Colors.info }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="provider-form"
          variant="contained"
          disabled={isBusy}
          sx={containedButtonSx}
        >
          {isBusy ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "inherit" }} />
              Saving...
            </Box>
          ) : mode === "add" ? (
            "Add Provider"
          ) : (
            "Save Changes"
          )}
        </Button>
      </Box>
    </Dialog>
  );
}
